import { Link } from 'react-router-dom';
import { usePickupBag } from '@/hooks/usePickupBag';
import { cdn } from '@/config/cdn';
import QuantityStepper from '@/pages/product/components/QuantityStepper';
import type { SendStatus, Submitted } from './PickupRequest';

type Props = {
  submitted: Submitted | null; date: string; formId: string; checked: boolean;
  configured: boolean; status: SendStatus; sendError: string; onSend: () => void;
};

const fmt = (n: number) => `$${n.toFixed(2)}`;
const fmtDate = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
const row = 'grid grid-cols-[minmax(0,1fr)_auto] gap-6 py-3.5 border-b border-[rgba(23,21,21,0.1)] text-sm';

export default function RequestSummary({ submitted, date, formId, checked, configured, status, sendError, onSend }: Props) {
  const bag = usePickupBag();
  const busy = status === 'submitting';
  const done = status === 'success' && !!submitted;
  const lines = done ? submitted.lines : bag.lines;
  const shownDate = done ? submitted.date : date;
  const totalQty = lines.reduce((s, l) => s + l.quantity, 0);
  const subtotal = lines.reduce((s, l) => s + l.quantity * l.product.price, 0);

  return (
    <aside className="bg-[#EDE7DE] px-5 py-6 md:px-8 md:py-8" aria-labelledby="summary-title">
      <div aria-live="polite">
        <p id="summary-title" className="fold-label text-[#5d5853] pb-4 border-b border-[rgba(23,21,21,0.16)] flex justify-between gap-4">
          <span>{done ? 'Request sent' : 'Request summary'}</span>
          <span className="tabular-nums">{lines.length} {lines.length === 1 ? 'bake' : 'bakes'}</span>
        </p>
        {lines.length === 0 ? (
          <div className="py-5 border-b border-[rgba(23,21,21,0.1)] text-sm text-[#5d5853]">
            <p>Your pickup bag is empty.</p>
            <Link to="/collection" className="mt-3 inline-block fold-label fold-link whitespace-nowrap cursor-pointer text-[#171515]">Browse the collection</Link>
          </div>
        ) : (
          <ul className="max-h-[420px] overflow-y-auto overscroll-contain">
            {lines.map(({ product, quantity }) => (
              <li key={product.slug} className="grid grid-cols-[56px_minmax(0,1fr)_auto] gap-x-4 py-4 border-b border-[rgba(23,21,21,0.1)]">
                <div className="w-14 h-14 overflow-hidden bg-[#e4e0da]">
                  <img src={cdn(product.coverImage)} alt={product.name} className="w-full h-full object-cover object-center" />
                </div>
                <div className="min-w-0">
                  <p className="fold-serif text-lg leading-tight">{product.name}</p>
                  <p className="mt-1 text-[11px] tracking-[0.14em] uppercase text-[#5d5853] tabular-nums">{fmt(product.price)} each{done ? ` · × ${quantity}` : ''}</p>
                  {!done && (
                    <div className={`mt-2 flex items-center gap-4 flex-wrap ${busy ? 'pointer-events-none opacity-50' : ''}`}>
                      <QuantityStepper value={quantity} onChange={(n) => bag.updateQuantity(product.slug, n)} label={`${product.name} quantity`} />
                      <button type="button" disabled={busy} onClick={() => bag.removeItem(product.slug)} aria-label={`Remove ${product.name}`} className="text-xs text-[#5d5853] underline underline-offset-4 decoration-[rgba(23,21,21,0.25)] hover:text-[#171515] whitespace-nowrap cursor-pointer">
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-sm tabular-nums mt-0.5">{fmt(product.price * quantity)}</p>
              </li>
            ))}
          </ul>
        )}
        <dl>
          <div className={row}><dt>Total pieces</dt><dd className="tabular-nums">{totalQty || '—'}</dd></div>
          <div className={row}><dt>Collection</dt><dd className="text-right">{shownDate ? fmtDate(shownDate) : '—'}</dd></div>
          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-6 pt-5 text-sm">
            <dt>Indicative subtotal</dt>
            <dd className="fold-serif text-2xl tabular-nums">{lines.length ? fmt(subtotal) : '—'}</dd>
          </div>
        </dl>
        <p className="mt-2 text-xs text-[#5d5853] leading-relaxed">Estimate only. Nothing is charged online — you pay at the counter.</p>
      </div>

      <div className="mt-8 flex flex-col gap-3">
        {checked && configured && !done ? (
          <button type="button" onClick={onSend} disabled={busy} aria-busy={busy} className="w-full whitespace-nowrap cursor-pointer disabled:cursor-wait disabled:opacity-70 bg-[#171515] text-[#F5F1EB] h-[52px] text-sm hover:bg-[#2e2a29] focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-[#171515] transition-colors">
            {busy ? 'Sending\u2026' : 'Send pickup request'}
          </button>
        ) : (
          <button type="submit" form={formId} disabled={done} className="w-full whitespace-nowrap cursor-pointer disabled:cursor-default disabled:opacity-70 bg-[#171515] text-[#F5F1EB] h-[52px] text-sm hover:bg-[#2e2a29] focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-[#171515] transition-colors">
            Review request
          </button>
        )}
        <p className="flex items-start gap-2 text-xs leading-relaxed text-[#45403c]">
          <i className="ri-information-line text-sm leading-none mt-px" aria-hidden="true"></i>
          <span>
            {configured
              ? 'Review checks your details first; nothing is sent until you press Send. A request is not a confirmed order — we\u2019ll be in touch.'
              : 'Pickup requests aren\u2019t set up on this site yet. You can review your details, but nothing can be sent.'}
          </span>
        </p>
        <div aria-live="polite">
          {checked && !done && status !== 'error' && (
            <p role="status" className="text-sm text-[#171515] border-l border-[#171515] pl-3 py-1">
              {configured ? 'Your details look complete. Press Send to submit your request.' : 'Your details look complete, but this site can\u2019t send requests yet. Nothing has been sent.'}
            </p>
          )}
          {done && (
            <p role="status" className="text-sm text-[#171515] border-l border-[#171515] pl-3 py-1">
              Pickup request received. Your preferred collection date is not yet confirmed.
            </p>
          )}
          {status === 'error' && (
            <p role="alert" className="text-sm text-[#171515] border-l border-[#171515] pl-3 py-1">
              {sendError} Your bag and details are still here — press Send to try again.
            </p>
          )}
        </div>
      </div>
    </aside>
  );
}